import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { type LucideIcon } from 'lucide-react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const optionButtonVariants = cva(
  'inline-flex icon-7 items-center justify-center rounded-lg border-0 bg-ui-bevel/60 text-text-gray transition-colors duration-150 hover:bg-ui-bevel hover:text-text-inverted disabled:pointer-events-none disabled:opacity-50 data-[state=open]:!bg-ui-bevel data-[state=open]:text-text-base! dark:bg-ui-bevel/60 dark:text-text-gray dark:hover:bg-ui-bevel dark:hover:text-text-base',
  {
    variants: {
      variant: {
        default: '',
        destructive: 'hover:bg-red-300 hover:text-red-800 dark:hover:bg-red-950 dark:hover:text-red-500',
      },
      size: {
        default: 'p-0',
        sm: 'icon-4',
        lg: 'icon-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface OptionActionProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof optionButtonVariants>,
    Pick<ButtonProps, 'tooltip' | 'tooltipClassName' | 'tooltipDelayDuration'> {
  'data-cy'?: string;
  'icon': LucideIcon;
}

const OptionAction = forwardRef<HTMLButtonElement, OptionActionProps>(
  ({ onClick, icon: Icon, variant, size, className, ...props }, ref) => (
    <Button
      ref={ref}
      onClick={onClick}
      data-cy={props['data-cy'] ?? 'option-action-button'}
      className={cn(optionButtonVariants({ variant, size }), className)}
      // If we don't specify the type="button" here,
      // the button will default to type="submit"
      // and trigger the form submission when clicked,
      // which is not the intended behavior.
      type="button"
      icon={Icon}
      iconClassName="icon-5"
      {...props}
    />
  ),
);

OptionAction.displayName = 'OptionAction';

export { OptionAction, optionButtonVariants };
